import React, { useState, useRef, useCallback, useEffect } from 'react';
import LightPoint from '../LightPoint/LightPoint';
import LedStripEditor from '../LedStripEditor/LedStripEditor';
import FooterControls from '../FooterControls/FooterControls';
import './PlantCanvas.css';

const PlantCanvas = ({
  imageUrl,
  lightPoints,          // já vem normalizado do App (instances -> elementos)
  onAddLightPoint,
  onSelectLightPoint,
  onDeleteLightPoint,
  selectedPointId,
  currentConfig,
  onQuickAdvanceNumber,
  onQuickResetNumber,
  onQuickNextSlot,
}) => {
  const canvasRef = useRef(null);

  // Editor LED controlado pelo pai via ref
  const editorRef = useRef(null);
  const [canCommitLed, setCanCommitLed] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [isEditingStrip, setIsEditingStrip] = useState(false);
  const [mouseCoords, setMouseCoords] = useState({ x: 0, y: 0 });

  // === UI MODE derivado ===
  const uiMode =
    currentConfig.lightType === 'ledStrip'
      ? isEditingStrip
        ? 'ledEditing'
        : 'ledIdle'
      : 'points';

  // Ao trocar de ferramenta, garantir que o editor LED seja fechado
  useEffect(() => {
    if (currentConfig.lightType !== 'ledStrip' && isEditingStrip) {
      setIsEditingStrip(false);
    }
  }, [currentConfig.lightType, isEditingStrip]);

  const toPct = (val, size) => Math.max(0, Math.min(100, (val / size) * 100));

  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = toPct(e.clientX - rect.left, rect.width);
    const y = toPct(e.clientY - rect.top, rect.height);
    setMouseCoords({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
  }, []);

  const handleCanvasClick = (e) => {
    if (isDragging || !imageUrl) return;

    // LED: primeiro clique entra em modo edição
    if (currentConfig.lightType === 'ledStrip') {
      setIsEditingStrip(true);
      return;
    }

    // Ponto normal
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = toPct(e.clientX - rect.left, rect.width);
    const y = toPct(e.clientY - rect.top, rect.height);

    onAddLightPoint({ x, y });
  };

  const handleMouseDown = () => setIsDragging(false);
  const handleMouseMoveWithDrag = (e) => {
    handleMouseMove(e);
    setIsDragging(true);
  };
  const handleMouseUp = () => setTimeout(() => setIsDragging(false), 50);

  const handleBackgroundClick = (e) => {
    if (e.target === canvasRef.current) {
      onSelectLightPoint(null);
    }
  };

  // ==== LED Editor integration ====
  const handleEditorCommit = useCallback(
    (d /* ledPath */, anchors) => {
      onAddLightPoint({
        ledPath: d,
        anchors: anchors,
      });
      setIsEditingStrip(false);
      setCanCommitLed(false);
    },
    [onAddLightPoint]
  );

  const handleEditorCancel = useCallback(() => {
    setIsEditingStrip(false);
    setCanCommitLed(false);
  }, []);

  const handleEditorCommitFromFooter = useCallback(() => {
    editorRef.current?.commit();
  }, []);

  // ==== Render helpers ====
  const renderLedStrip = (point) => {
    const isSelected =
      selectedPointId &&
      (point.groupId === selectedPointId || point.id === selectedPointId);

    return (
      <div
        key={point.id}
        className={`led-strip-wrapper ${isSelected ? 'selected' : ''}`}
        style={{
          left: '0%',
          top: '0%',
          width: '100%',
          height: '100%',
          position: 'absolute',
          zIndex: 5,
        }}
      >
        <svg
          className="led-strip-view"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* Caminho visível */}
          <path
            d={point.ledPath}
            fill="none"
            stroke={isSelected ? '#007bff' : point.ledColor || '#f4bb00'}
            strokeWidth={point.ledStrokeWidth || 2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            className="led-strip-path"
          />
          {/* Hitbox para seleção */}
          <path
            d={point.ledPath}
            fill="none"
            stroke="rgba(0,0,0,0)"
            strokeWidth={(point.ledStrokeWidth || 2.5) * 2}
            vectorEffect="non-scaling-stroke"
            className="led-strip-hitbox"
            onClick={(e) => {
              e.stopPropagation();
              onSelectLightPoint(point.id);
            }}
          />
        </svg>

        {/* Botão deletar quando selecionado */}
        {isSelected && (
          <button
            className="delete-btn"
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 20,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteLightPoint(point.id);
            }}
            title="Remover fita LED"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  // ==== Número/Slots (apenas em 'points') ====
  const handleDecreaseNumber = () => {
    if (currentConfig.circleNumber > 1) {
      onQuickResetNumber();
    }
  };

  return (
    <div className="plant-canvas-container">
      <div
        ref={canvasRef}
        className={`plant-canvas ${
          currentConfig.lightType === 'ledStrip'
            ? 'placing-led-strip'
            : 'placing-normal'
        } mode-${uiMode}`}
        onClick={(e) => {
          handleBackgroundClick(e);
          handleCanvasClick(e);
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMoveWithDrag}
        onMouseUp={handleMouseUp}
      >
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt="Planta do condomínio"
              className="plant-image"
              draggable={false}
            />

            {/* Coordenadas em tempo real */}
            <div className="coordinates-display">
              📍 {mouseCoords.x}%, {mouseCoords.y}%
            </div>

            {/* Renderizar pontos normais */}
            {lightPoints
              .filter((p) => !p.isLedStrip)
              .map((point) => (
                <LightPoint
                  key={point.id}
                  id={point.id}
                  x={point.x}
                  y={point.y}
                  width={point.width}
                  height={point.height}
                  circleNumber={point.circleNumber}
                  descricao={point.descricao}
                  isSelected={
                    point.groupId === selectedPointId ||
                    point.id === selectedPointId
                  }
                  onClick={onSelectLightPoint}
                  onDelete={onDeleteLightPoint}
                />
              ))}

            {/* Renderizar fitas LED */}
            {lightPoints.filter((p) => p.isLedStrip).map(renderLedStrip)}

            {/* Editor de LED Strip ativo */}
            {isEditingStrip && (
              <LedStripEditor
                ref={editorRef}
                active={true}
                containerRef={canvasRef}
                color={currentConfig.ledColor || '#f4bb00'}
                strokeWidth={Number(currentConfig.ledStrokeWidth) || 2.5}
                onCommit={handleEditorCommit}
                onCancel={handleEditorCancel}
                onStateChange={({ canCommit }) => setCanCommitLed(canCommit)}
              />
            )}

            {/* Grade de auxílio */}
            <div className="grid-overlay" />

            {/* Footer Controls (apresentacional) */}
            <FooterControls
              uiMode={uiMode}
              // points
              circleNumber={currentConfig.circleNumber}
              canDecrease={currentConfig.circleNumber > 1}
              onPrevNumber={handleDecreaseNumber}
              onNextNumber={onQuickAdvanceNumber}
              onNextSlot={onQuickNextSlot}
              // led
              canCommitLed={canCommitLed}
              onLedCancel={handleEditorCancel}
              onLedCommit={handleEditorCommitFromFooter}
            />
          </>
        ) : (
          <div className="placeholder">
            <div className="placeholder-content">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
              <h3>Carregue uma planta</h3>
              <p>Arraste uma imagem aqui ou use o botão de upload</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlantCanvas;